import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { getPropertyName } from '@/lib/utils'
import dayjs from 'dayjs'

export const useFetchAndStoreAsync = async(entities, getFunctionAsync, store = false) => {
  const res = await getFunctionAsync()
  if (store && res.data) {
    entities.value = res.data
  }
  return res?.data
}

export const useCheckLastUpdate = async(stores, promises, functionNameFn = null, force = false) => {
  const webSiteStore = useWebSiteStore()

  if (!Array.isArray(stores)) {
    stores = [stores]
  }

  if ((!webSiteStore.lastUpdates || !webSiteStore.lastUpdatesTimestamp || dayjs().diff(webSiteStore.lastUpdatesTimestamp, 'seconds') > window.config.CACHE_TIMEOUT) || force) {
    await webSiteStore.fetchLastUpdateAsync(true)
    webSiteStore.lastUpdatesTimestamp = Date.now()
  }

  const storeIds = stores.map(store => store.$id)

  webSiteStore.lastUpdates.filter(x => storeIds.some(id => x.model.startsWith(id))).forEach(x => {
    const modelName = x.model.split('.').pop()
    const lastLocalUpdate = webSiteStore.lastUpdateDictionary[x.model]
    if (!lastLocalUpdate || lastLocalUpdate !== x.updated_at) {
      const mappedName = functionNameFn && functionNameFn(modelName)
      const functionName = mappedName || `fetch${modelName}sAsync`
      let found = false
      stores.forEach(store => {
        // An explicit mapping already pins the exact function, which may live on a
        // store whose $id doesn't prefix the model (e.g. portal.Country → web_site store).
        const matches = mappedName ? !!store[functionName] : (x.model.startsWith(store.$id) && store[functionName])
        if (matches) {
          found = true
          promises.push(
            store[functionName]({all: true}, true).then(() => {
              webSiteStore.lastUpdateDictionary[x.model] = x.updated_at
            })
          )
        }
      })
      if (!found) {
        console.warn(`No fetch function found for model ${modelName} with functionName: ${functionName}`)
      }
    }
  })

  return promises
}

export const refreshAllStoresAsync = async() => {
  const accountingStore = useAccountingStore()
  const planningStore = usePlanningStore()
  const portalStore = usePortalStore()
  const scheduleStore = useScheduleStore()
  const webSiteStore = useWebSiteStore()

  const promises = []

  const storesToUpdate = [accountingStore, planningStore, portalStore, scheduleStore, webSiteStore]

  await useCheckLastUpdate(
    storesToUpdate,
    promises,
    (modelName) => {
      switch (modelName) {
        case 'BudgetDocumentRowCategory': return getPropertyName(accountingStore, x => x.fetchBudgetDocumentRowCategoriesAsync)
        case 'CompanyData': return getPropertyName(accountingStore, x => x.fetchCompanyDataAsync)
        case 'Company': return getPropertyName(planningStore, x => x.fetchCompaniesAsync)
        case 'Country': return getPropertyName(webSiteStore, x => x.fetchCountriesAsync)
        case 'Currency': return getPropertyName(webSiteStore, x => x.fetchCurrenciesAsync)
        case 'PortalServiceTag': return getPropertyName(portalStore, x => x.fetchTagsAsync)
        case 'UnitOfMeasure': return getPropertyName(webSiteStore, x => x.fetchUnitsOfMeasureAsync)
        default: return null
      }
    },
    true
  )

  await Promise.all(promises)
}
